(ns common.db.asset-upload.resumable
  (:require ["spark-md5$default" :as spark-md5]
            [common.url :as url]
            [goog.crypt.base64 :as base64]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(def part-size (* 5 1024 1024)) ; 5MB, minimum size for parts to be uploaded

(defn get-bytes
  [file offset full-size part-size]
  (.slice file
          (js/parseInt offset)
          (min full-size
               (+ (js/parseInt offset) part-size))))

(defn upload-metadata
  [{:keys [file-name digest public confirm unzip]
    :or {confirm false
         unzip false}}]
  (str "filename " (base64/encodeString file-name)
       ",digest " (base64/encodeString digest)
       ",public " (base64/encodeString public)
       ",confirm " (base64/encodeString confirm)
       ",unzip " (base64/encodeString unzip)))

(defn calculate-md5
  "Calculate the MD5 of a file incrementally"
  [file bufferSize]
  (let [file-reader (js/FileReader.)
        file-slicer (or
                     (.. js/File -prototype -slice)
                     (.. js/File -prototype -mozSlice)
                     (.. js/File -prototype -webkitSlice))
        md5 (spark-md5.)
        total-parts (.ceil js/Math (/ (.-size file) bufferSize))
        current-part (r/atom 0)]
    (js/Promise.
     (fn [resolve reject]
       (set! (.-onload file-reader)
             (fn [e]
               (reset! current-part (inc @current-part))
               (.appendBinary md5 (.. e -target -result))

               (if (< @current-part total-parts)
                 (let [start (* @current-part bufferSize)
                       end (.min js/Math (+ start bufferSize) (.-size file))]
                   (.readAsBinaryString file-reader (.call file-slicer file start end)))
                 (resolve (.end md5)))))

       (set! (.-onerror file-reader)
             (fn [e]
               (reject e)))

       (let [start (* @current-part bufferSize)
             end (.min js/Math (+ start bufferSize) (.-size file))]
         (.readAsBinaryString file-reader (.call file-slicer file start end)))))))

(defn start-upload
  [{:keys [uri path on-change on-progress on-failure file public unzip]
    :or {public true
         unzip false}}]
  (-> file
      (calculate-md5 part-size)
      (.then
       (fn [hash-result]
         (rf/dispatch [:asset-upload.resumable/start {:uri uri
                                                      :file file
                                                      :on-change on-change
                                                      :on-progress on-progress
                                                      :on-failure on-failure
                                                      :path path
                                                      :digest hash-result
                                                      :public public
                                                      :unzip unzip}])))))

(defn calc-progress [offset size]
  (let [*offset (if (or (nil? offset) (js/Number.isNaN offset)) size offset)]
    (* 100 (/ *offset size))))

(rf/reg-event-fx :asset-upload.resumable/start
  (fn [{:keys [db]} [_ {:keys [uri path file on-change on-progress on-failure digest public unzip]}]]
    (let [bytes (.slice file 0 part-size)
          size (.-size file)
          initial-state {:file file
                         :size size
                         :on-change on-change
                         :on-progress on-progress
                         :on-failure on-failure
                         :progress {:state :pending
                                    :value 0}}]
      {:db (-> db
               (assoc-in [:asset-upload.resumable digest] initial-state)
               (assoc-in [:asset-upload.resumable :digest path] digest))
       :fx [[:dispatch [:asset-upload.resumable/create {:uri uri
                                                        :file-name (.-name file)
                                                        :digest digest
                                                        :bytes bytes
                                                        :size size
                                                        :public public
                                                        :unzip unzip}]]]})))

(rf/reg-event-fx :asset-upload.resumable/create
  [(rf/inject-cofx :sub :resource.user/token)]
  (fn [{:resource.user/keys [token]} [_ {:keys [uri size file-name digest bytes public unzip]}]]
    {:http
     {:method :post
      :uri uri
      :headers {"Tus-Resumable" "1.0.0"
                "Upload-Length" size
                "X-Requested-With" "XMLHttpRequest"
                "Upload-Metadata" (upload-metadata {:file-name file-name :digest digest :public public :unzip unzip})
                :Authorization token}
      :body bytes
      :format :binary-with-offset
      :response-format :asset-create
      :on-success [:asset-upload.resumable/create-success digest]
      :on-failure [:asset-upload.resumable/create-failure digest]}}))

(rf/reg-event-fx :asset-upload.resumable/create-success
  (fn [{:keys [db]} [_ digest response]]
    (let [{:keys [location offset url status]} response
          size (get-in db [:asset-upload.resumable digest :size])
          on-change (get-in db [:asset-upload.resumable digest :on-change])
          on-progress (get-in db [:asset-upload.resumable digest :on-progress])
          db* (if (= status 200)
                (-> db
                    (assoc-in [:asset-upload.resumable digest :url] url)
                    (assoc-in [:asset-upload.resumable digest :progress] {:state :success}))
                (-> db
                    (assoc-in [:asset-upload.resumable digest :location] location)
                    (assoc-in [:asset-upload.resumable digest :progress] {:state :pending
                                                                          :value offset})))]
      {:db db*
       :fx (if (= status 200)
             [(when on-progress [:dispatch (conj on-progress (calc-progress offset size))])
              [:upload/on-change [on-change url]]]
             [[:dispatch [:asset-upload.resumable/upload-part {:digest digest :offset offset}]]])})))

(rf/reg-event-fx :asset-upload.resumable/create-failure
  (fn [{:keys [db]} [_ digest e]]
    (let [on-failure (get-in db [:asset-upload.resumable digest :on-failure])]
      {:db (assoc-in db [:asset-upload.resumable digest :progress :state] :failure)
       :fx [(when on-failure
              [:dispatch (conj on-failure e)])]})))

(rf/reg-event-fx :asset-upload.resumable/upload-part
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :config)]
  (fn [{:keys [db config] :as cofx} [_ {:keys [digest offset]
                                        :or {offset 0}}]]
    (let [{:keys [location file size]} (get-in db [:asset-upload.resumable digest])
          bytes (get-bytes file offset size part-size)]
      {:http
       {:method :patch
        :uri (url/parts->str [(:apiUrl config) location])
        :headers {"Tus-Resumable" "1.0.0"
                  "Content-Length" part-size
                  "Content-Type" "application/offset+octet-stream"
                  "Upload-Offset" offset
                  :Authorization (:resource.user/token cofx)}
        :body bytes
        :format :binary-with-offset
        :response-format :upload-part
        :on-success [:asset-upload.resumable/upload-part-success digest]
        :on-failure [:asset-upload.resumable/upload-part-failure digest]}})))

(rf/reg-event-fx :asset-upload.resumable/upload-part-success
  (fn [{:keys [db]} [_ digest response]]
    (let [offset (js/parseInt (:offset response))
          size (get-in db [:asset-upload.resumable digest :size])
          on-progress (get-in db [:asset-upload.resumable digest :on-progress])]
      {:db (assoc-in db [:asset-upload.resumable digest :progress] {:state :pending
                                                                    :value offset})
       :fx [(when on-progress [:dispatch (conj on-progress (calc-progress offset size))])
            (if (= (:status response) 200)
              [:dispatch [:asset-upload.resumable/finished digest response]]
              [:dispatch [:asset-upload.resumable/upload-part {:digest digest
                                                               :offset offset}]])]})))

(rf/reg-event-db :asset-upload.resumable/upload-part-failure
  (fn [db [_ digest _]]
    (assoc-in db [:asset-upload.resumable digest :progress :state] :failure)))

(rf/reg-event-fx :asset-upload.resumable/finished
  (fn [{:keys [db]} [_ digest response]]
    (let [on-change (get-in db [:asset-upload.resumable digest :on-change])]
      {:db (assoc-in db [:asset-upload.resumable digest :progress :state] :success)
       :fx [[:upload/on-change [on-change (:url response)]]]})))

(rf/reg-sub :asset-upload.resumable/progress
  (fn [db [_ path]]
    (let [digest (get-in db [:asset-upload.resumable :digest path])]
      (get-in db [:asset-upload.resumable digest :progress]))))

(rf/reg-event-db :asset-upload.resumable/destroy
  (fn [db [_ path]]
    (let [digest (get-in db [:asset-upload.resumable :digest path])]
      (-> db
          (update-in [:asset-upload.resumable digest] dissoc :progress)
          (update-in [:upload :meta] dissoc path)))))
